export interface WatchdogState {
    readonly lastPongAt: number;
    readonly strikes: number;
}

export interface WatchdogTick {
    readonly state: WatchdogState;
    readonly forceReconnect: boolean;
}

// Decide whether the WebSocket should be force-reconnected based on how long
// it has been since the last server pong. The two-strike rule tolerates a
// single missed window (GC pauses, browser tab throttling) without forcing a
// spurious reconnect.
export function watchdogTick(
    now: number,
    state: WatchdogState,
    timeoutMs: number,
): WatchdogTick {
    const silenceMs = now - state.lastPongAt;
    if (silenceMs > timeoutMs) {
        const strikes = state.strikes + 1;
        return {
            state: { ...state, strikes },
            forceReconnect: strikes >= 2,
        };
    }
    return {
        state: { ...state, strikes: 0 },
        forceReconnect: false,
    };
}

// Decide whether the watchdog should disable itself because the server never
// answered the initial ping. Used for graceful degradation when the backend
// does not (yet) support the pong response. Uses <=: when the hook
// initialises lastPongAt to Date.now() at onopen, only a pong arriving
// strictly after that bumps lastPongAt; if it stays equal, the server has
// not answered.
export function shouldDisableAfterHandshake(
    now: number,
    connectionStartedAt: number,
    lastPongAt: number,
    handshakeTimeoutMs: number,
): boolean {
    if (now - connectionStartedAt < handshakeTimeoutMs) return false;
    return lastPongAt <= connectionStartedAt;
}
